/**
 * SEC-07 / SEC-08 (SCRUM-352, 353): "my data" controls in the personal area.
 *
 *  - הורדת המידע שלי  -> GET  client-api/account/export/  (JSON file)
 *  - מחיקת חשבון      -> POST client-api/account/delete/request-code/ + delete/
 *                        (SMS code, or a login in the last 15 minutes when
 *                        the account has no phone), 14-day grace period,
 *                        logging in again cancels it.
 */
import { Component, OnInit, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { ApiUrl } from '../../shared/models/apiurl.models';
import { SystemApp } from '../../constans/SystemConfigurations';
import { AuthSessionService } from '../../services/auth-session.service';
import { ToastService } from '../../shared/toast/toast.service';

interface DeletionStatus {
  pending: boolean;
  scheduled_for: string | null;
  grace_days: number;
  confirm_with: 'sms_code' | 'recent_login';
}

@Component({
  selector: 'app-privacy-controls',
  standalone: true,
  imports: [FormsModule],
  template: `
  <section class="privacy-panel" aria-labelledby="privacy-title" dir="rtl">
    <h3 id="privacy-title" class="privacy-panel__title">המידע שלי והחשבון</h3>

    <div class="privacy-panel__row">
      <div>
        <strong>הורדת המידע שלי</strong>
        <p class="privacy-panel__hint">קובץ עם כל המידע האישי שאנחנו שומרים על החשבון שלך.</p>
      </div>
      <button type="button" class="privacy-panel__btn" [disabled]="exporting" (click)="download()">
        {{ exporting ? 'מכין קובץ…' : 'הורדה' }}
      </button>
    </div>

    <div class="privacy-panel__row privacy-panel__row--danger">
      <div>
        <strong>מחיקת חשבון</strong>
        @if (status?.pending) {
          <p class="privacy-panel__hint">
            החשבון יימחק ב-{{ scheduledDate }}. עד אז אפשר לבטל, או פשוט להתחבר שוב.
          </p>
        } @else {
          <p class="privacy-panel__hint">
            לאחר הבקשה תנותק מכל המכשירים. בתוך {{ status?.grace_days || 14 }} ימים אפשר לבטל
            על ידי התחברות מחדש. אחר כך הפרטים האישיים נמחקים והמודעות יורדות.
            רשומות תשלום וחשבוניות נשמרות כנדרש בחוק, ללא פרטים מזהים.
          </p>
        }
      </div>
      @if (status?.pending) {
        <button type="button" class="privacy-panel__btn" [disabled]="busy" (click)="cancel()">ביטול המחיקה</button>
      } @else if (!open) {
        <button type="button" class="privacy-panel__btn privacy-panel__btn--danger" (click)="open = true">מחיקת החשבון</button>
      }
    </div>

    @if (open && !status?.pending) {
      <div class="privacy-panel__confirm">
        @if (status?.confirm_with === 'sms_code') {
          @if (!codeSent) {
            <button type="button" class="privacy-panel__btn" [disabled]="busy" (click)="sendCode()">שליחת קוד אימות ב-SMS</button>
          } @else {
            <label>
              קוד שנשלח לטלפון
              <input [(ngModel)]="code" inputmode="numeric" autocomplete="one-time-code" maxlength="6" dir="ltr">
            </label>
          }
        } @else {
          <p class="privacy-panel__hint">לאישור, יש להתחבר מחדש בדקות האחרונות.</p>
        }
        <label>
          סיבה (לא חובה)
          <input [(ngModel)]="reason" maxlength="300">
        </label>
        <div class="privacy-panel__actions">
          <button type="button" class="privacy-panel__btn privacy-panel__btn--danger"
                  [disabled]="busy || (status?.confirm_with === 'sms_code' && code.length < 6)"
                  (click)="confirmDelete()">אני מאשר/ת למחוק את החשבון</button>
          <button type="button" class="privacy-panel__btn privacy-panel__btn--ghost" (click)="open = false">ביטול</button>
        </div>
      </div>
    }
    @if (message) {
      <p class="privacy-panel__status" role="alert">{{ message }}</p>
    }
  </section>
  `,
  styles: [`
    .privacy-panel { margin-top: 24px; padding: 18px 20px; background: var(--input-bg);
      border: 1px solid var(--border); border-radius: var(--radius-md); text-align: right; }
    .privacy-panel__title { margin: 0 0 12px; color: var(--text-main); }
    .privacy-panel__row { display: flex; gap: 16px; align-items: center; justify-content: space-between;
      padding: 12px 0; border-top: 1px solid var(--border); flex-wrap: wrap; }
    .privacy-panel__hint { margin: 4px 0 0; font-size: 13px; color: var(--text-muted, #666); max-width: 520px; }
    .privacy-panel__btn { padding: 8px 16px; border-radius: var(--radius-md); border: 1px solid var(--border);
      background: var(--surface, #fff); cursor: pointer; white-space: nowrap; }
    .privacy-panel__btn:disabled { opacity: .6; cursor: default; }
    .privacy-panel__btn--danger { border-color: #c62828; color: #c62828; }
    .privacy-panel__btn--ghost { border-color: transparent; background: transparent; }
    .privacy-panel__confirm { display: grid; gap: 10px; padding: 12px 0 0; }
    .privacy-panel__confirm label { display: grid; gap: 4px; font-size: 14px; }
    .privacy-panel__confirm input { padding: 8px; border: 1px solid var(--border); border-radius: 8px; max-width: 280px; }
    .privacy-panel__actions { display: flex; gap: 8px; flex-wrap: wrap; }
    .privacy-panel__status { margin: 10px 0 0; color: #c62828; }
  `]
})
export class PrivacyControlsComponent implements OnInit {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = inject(ApiUrl);
  private readonly authSession = inject(AuthSessionService);
  private readonly toast = inject(ToastService);
  private readonly router = inject(Router);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly base = `${SystemApp.base_url_api}/client-api/account`;

  status: DeletionStatus | null = null;
  open = false;
  codeSent = false;
  code = '';
  reason = '';
  busy = false;
  exporting = false;
  message = '';

  private get headers() {
    return new HttpHeaders({ Authorization: this.apiUrl.getJWTToken() });
  }

  get scheduledDate(): string {
    return this.status?.scheduled_for ? new Date(this.status.scheduled_for).toLocaleDateString('he-IL') : '';
  }

  ngOnInit(): void {
    if (this.isBrowser) this.loadStatus();
  }

  private async loadStatus() {
    try {
      this.status = await firstValueFrom(
        this.http.get<DeletionStatus>(`${this.base}/delete/status/`, { headers: this.headers }));
    } catch {
      this.status = null;
    }
  }

  async download() {
    if (!this.isBrowser || this.exporting) return;
    this.exporting = true;
    this.message = '';
    try {
      const blob = await firstValueFrom(
        this.http.get(`${this.base}/export/`, { headers: this.headers, responseType: 'blob' }));
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sale4all-my-data-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e: any) {
      this.message = e?.status === 429 ? 'אפשר להוריד את הקובץ עד 3 פעמים ביום.' : 'ההורדה נכשלה, נסו שוב מאוחר יותר.';
    } finally {
      this.exporting = false;
    }
  }

  async sendCode() {
    this.busy = true;
    this.message = '';
    try {
      await firstValueFrom(this.http.post(`${this.base}/delete/request-code/`, {}, { headers: this.headers }));
      this.codeSent = true;
    } catch (e: any) {
      this.message = e?.status === 429 ? 'יותר מדי בקשות, נסו שוב בעוד שעה.' : 'שליחת הקוד נכשלה.';
    } finally {
      this.busy = false;
    }
  }

  async confirmDelete() {
    this.busy = true;
    this.message = '';
    try {
      const res: any = await firstValueFrom(this.http.post(`${this.base}/delete/`,
        { code: this.code.trim(), reason: this.reason }, { headers: this.headers, withCredentials: true }));
      const date = res?.scheduled_for ? new Date(res.scheduled_for).toLocaleDateString('he-IL') : '';
      this.toast.success(`החשבון יימחק ב-${date}. התחברות מחדש עד אז מבטלת את המחיקה.`);
      this.authSession.logout().subscribe({ complete: () => this.router.navigate(['/']) });
    } catch (e: any) {
      const code = e?.error?.statusCode;
      this.message = code === 'INVALID_CODE' ? 'הקוד שגוי או שפג תוקפו.'
        : code === 'RELOGIN_REQUIRED' ? 'יש להתנתק, להתחבר מחדש ולאשר בתוך 15 דקות.'
        : code === 'ADMIN_ACCOUNT' ? 'חשבון ניהול לא נמחק מכאן - פנו למנהל אחר.'
        : 'המחיקה נכשלה, נסו שוב.';
    } finally {
      this.busy = false;
    }
  }

  async cancel() {
    this.busy = true;
    try {
      await firstValueFrom(this.http.post(`${this.base}/delete/cancel/`, {}, { headers: this.headers }));
      this.toast.success('בקשת המחיקה בוטלה.');
      await this.loadStatus();
    } catch {
      this.message = 'הביטול נכשל, נסו שוב.';
    } finally {
      this.busy = false;
    }
  }
}
